import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { AutoAwesome as InsightIcon } from '@mui/icons-material';

import type { HeadlineResult } from '../../utils/intelligence';
import { intelligenceCopy } from '../../copy/intelligence';

// ---------------------------------------------------------------------------
// HeadlineInsight — the one-line signal that leads every Intelligence-cluster
// report. Keeps the user focused on what the numbers mean before they see the
// numbers themselves. When the derivation returns `null` (not enough data yet)
// we render a neutral note so the layout doesn't collapse into the chart.
// ---------------------------------------------------------------------------

interface HeadlineInsightProps {
  result: HeadlineResult | null;
  variant?: 'primary' | 'compact';
}

export function HeadlineInsight({ result, variant = 'primary' }: HeadlineInsightProps) {
  const isCompact = variant === 'compact';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: isCompact ? 1.5 : 2,
        mb: 2.5,
        borderRadius: 2,
        borderColor: 'primary.main',
        bgcolor: 'rgba(108, 155, 255, 0.04)',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <InsightIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              letterSpacing: '0.12em',
              fontWeight: 600,
              lineHeight: 1,
              mb: 0.5,
            }}
          >
            {intelligenceCopy.headlineLabel}
          </Typography>
          {result ? (
            <>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {result.headline}
              </Typography>
              {result.delta && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 0.5 }}
                >
                  {result.delta}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {intelligenceCopy.noSignalYet}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {intelligenceCopy.noSignalHint}
              </Typography>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default HeadlineInsight;
